<template>
  <div class="w-full flex flex-col gap-4 text-left">
    <div class="w-full flex flex-wrap items-center justify-between gap-4">
      <div class="flex-grow min-w-[260px] sm:flex-grow-0 sm:w-2/5">
        <SearchInput
          v-model="textInput"
          @search-click="handleSearchIconClick"
          width="100%"
          :loading="isLoading"
          :label="`Buscar ${tabsName[activeTab]}`"
          hide-details
          density="compact"
          spellcheck="false"
          data-testid="users-search-input"
        />
      </div>
      <div class="flex-shrink-0">
        <FilledButton
          @click="toggleCreateModal"
          :text="`Criar ${tabsName[activeTab]}`"
          width="180px"
          height="40px"
          :disabled="!canUserCreating"
          class="!bg-primary30"
          data-testid="create-user-button"
        />
      </div>
    </div>
    <div>
      <div class="w-full h-full my-0">
        <Tabs v-model="activeTab" :tabs="tabList">
          <template #users>
            <UserTab />
          </template>
          <template #groups>
            <GroupTab />
          </template>
        </Tabs>

        <CreateUserModal @close="toggleModalCreateUser" :modalActive="showModalCreateUser" />
        <DeleteUserModal @close="toggleModalUsersGroup" :modalStateProp="showModalUsersGroup" />
      </div>
    </div>
  </div>
  <teleport to="#app">
    <CreateGroupModal />
    <SubjectPermissionsTreeModal />
  </teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
// Group
import GroupTab from './group/group.tab.vue'
import CreateGroupModal from './group/modal/create.group.modal.vue'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import CreateUserModal from './components/CreateUserModal/Index.vue'
import UserTab from './user.tab.vue'
import DeleteUserModal from './components/DeleteUserModal/Index.vue'
import SubjectPermissionsTreeModal from './components/PermissionsTreeModal/index.vue'

import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import { useGroupStore } from '@/pinia-store/useGroupStore'
import { useUserStore } from '@/pinia-store/useUsersStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'

const modalStore = useModalStore()
const permissionsStore = usePermissionsStore()
const headerStore = useHeaderStore()

const showModalCreateUser = ref(false)
const showModalUsersGroup = ref(false)
const textInput = ref('')

type UsersTabValue = 'users' | 'groups'

const activeTab = ref<UsersTabValue>('users')
const tabsName = ref<Record<UsersTabValue, string>>({ users: 'usuários', groups: 'grupos' })
const tabList: TabItem[] = [
  { label: 'Usuários', value: 'users', testId: 'users-tab-button' },
  { label: 'Grupos', value: 'groups', testId: 'groups-tab-button' }
]

const userStore = useUserStore()
const groupStore = useGroupStore()

const isLoading = computed(() => {
  if (activeTab.value === 'users') {
    return userStore.isLoading
  }
  if (activeTab.value === 'groups') {
    return groupStore.isLoading || false
  }
  return false
})

let debounceTimer: NodeJS.Timeout

const formatSearchText = (text: string): string => text.toLowerCase().trim()

watch(textInput, (searchText: string) => {
  clearTimeout(debounceTimer)

  debounceTimer = setTimeout(() => {
    const formattedText = formatSearchText(searchText)

    if (activeTab.value === 'users') {
      userStore.setSearchTerm(formattedText)
      userStore.fetchUsers()
    } else if (activeTab.value === 'groups') {
      groupStore.applyFilters({ searchTerm: formattedText })
    }
  }, 500)
})

const handleSearchIconClick = () => {
  clearTimeout(debounceTimer)
  userStore.setSearchTerm(textInput.value)
  userStore.fetchUsers()
}

watch(activeTab, (newTab: UsersTabValue) => {
  textInput.value = ''
  if (newTab === 'users') {
    userStore.setSearchTerm('')
    userStore.fetchUsers()
  } else if (newTab === 'groups') {
    groupStore.applyFilters({ searchTerm: '' })
  }
})

const toggleCreateModal = () => {
  if (activeTab.value === 'users') {
    showModalCreateUser.value = !showModalCreateUser.value
    return
  }
  modalStore.toggleModal(EModals.CREATE_GROUP_MODAL)
}

const toggleModalCreateUser = () => {
  showModalCreateUser.value = !showModalCreateUser.value
}

const toggleModalUsersGroup = () => {
  showModalUsersGroup.value = !showModalUsersGroup.value
}

onMounted(() => {
  headerStore.setTitle(
    'Usuários e Grupos',
    'Gerencie usuários, grupos e permissões de acesso ao sistema'
  )

  userStore.fetchUsers()
})

const canUserCreating = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyUserCreating,
    resourceType: AuthorizationResourcesAndSubjects.Company
  })
})
</script>
